import { colors } from '@/constants/colors';
import { Icon } from '@/components/shared/display/Icon';
import {
  BellExclamationSolidFilledIcon,
  CircleCheckSolidFilledIcon,
  CircleQuestionSolidFilledIcon,
  HexagonXmarkSolidFilledIcon,
  MessageExclamationSolidFilledIcon,
} from './assets/Icons';

export const SIDEBAR_MENU_BUTTON_SIZE = '24px';

export enum MIME_TYPE {
  Video_MP4 = 'video/mp4',
  Image_PNG = 'image/png',
  Image_JPG = 'image/jpg',
}

export enum HEALTH_STATUS {
  CRITICAL = 'critical',
  ALARM = 'alarm',
  MONITOR = 'monitor',
  HEALTHY = 'healthy',
  NOT_AVAILABLE = 'na',
}

export enum SENSOR_STATUS {
  ONLINE = 'online',
  OFFLINE = 'offline',
  DATA_RECEIVED = 'data_received',
  DATA_NOT_RECEIVED = 'data_not_received',
}

export enum FUSION_TRENDS_TYPE {
  Machine = 'machine',
  Component = 'component',
}

export const REGULAR_DATE_FORMAT = 'dd.MM.yyyy';
export const SHORT_YEAR_DATE_FORMAT = 'dd.MM.yy';
export const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm';
export const DATE_TIME_AM_PM_FORMAT = 'dd.MM.yyyy ha';
export const MMMM_DD_YYYY_FORMAT = 'MMMM dd, yyyy';
export const MMMM_DD_FORMAT = 'MMMM dd';

type HealthState = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
};

export const HEALTH_STATES: Record<string, HealthState> = {
  [HEALTH_STATUS.CRITICAL]: {
    icon: HexagonXmarkSolidFilledIcon,
    title: 'general.legend.list.critical.title',
    description: 'general.legend.list.critical.description',
  },
  [HEALTH_STATUS.ALARM]: {
    icon: BellExclamationSolidFilledIcon,
    title: 'general.legend.list.alarm.title',
    description: 'general.legend.list.alarm.description',
  },
  [HEALTH_STATUS.MONITOR]: {
    icon: MessageExclamationSolidFilledIcon,
    title: 'general.legend.list.monitor.title',
    description: 'general.legend.list.monitor.description',
  },
  [HEALTH_STATUS.HEALTHY]: {
    icon: CircleCheckSolidFilledIcon,
    title: 'general.legend.list.healthy.title',
    description: 'general.legend.list.healthy.description',
  },
  [HEALTH_STATUS.NOT_AVAILABLE]: {
    icon: CircleQuestionSolidFilledIcon,
    title: 'general.legend.list.notAvailable.title',
    description: 'general.legend.list.notAvailable.description',
  },
  offline: {
    icon: HexagonXmarkSolidFilledIcon,
    title: 'general.sensorStatus.offline',
    description: 'general.legend.list.critical.description',
  },
  online: {
    icon: CircleCheckSolidFilledIcon,
    title: 'general.sensorStatus.online',
    description: 'general.legend.list.healthy.description',
  },
  data_not_received: {
    icon: HexagonXmarkSolidFilledIcon,
    title: 'general.sensorStatus.data_not_received',
    description: 'general.legend.list.critical.description',
  },
  data_received: {
    icon: CircleCheckSolidFilledIcon,
    title: 'general.sensorStatus.data_received',
    description: 'general.legend.list.healthy.description',
  },
};

export enum TOAST_STATUS {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export enum MANAGEMENT_ROLE {
  DM_Admin = 'dm_admin',
  Admin = 'admin',
  Member = 'member',
  DM_Expert = 'dm_expert',
}

export enum ENVIRONMENTS {
  Production = 'production',
  Development = 'development',
  Test = 'test',
}

export const plotlyColors = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
];

export const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export enum SENSORS_DATA_TYPE {
  RMS = 'rms',
  FFT = 'fft',
  PI = 'pi',
  Camera = 'camera',
  SensorFusion = 'sensorFusion',
  VisualAI = 'visualAI',
  OilReports = 'oil report',
}

export enum SENSOR_TYPE {
  Camera = 'camera',
  Vibrations = 'vibrations',
  Vibration = 'vibration',
  OilReport = 'oil report',
}

export enum FAILURE_NAME {
  GapSize = 'gap size',
  BeltShift = 'belt shift',
  BeltShiftLeft = 'belt shift to the left',
  BeltShiftRight = 'belt shift to the right',
}

export const statusColors: Record<string, string> = {
  critical: colors.health.critical,
  alarm: colors.health.alarm,
  monitor: colors.health.monitor,
  healthy: colors.health.healthy,
  na: colors.health.notAvailable,
};

export const DIAGNOSTICS_REGULAR_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
export const DIAGNOSTICS_FROM_DATE_FORMAT = "yyyy-MM-dd'T'00:00:00";
export const DIAGNOSTICS_TO_DATE_FORMAT = "yyyy-MM-dd'T'23:59:59";
export const DIAGNOSTICS_DATE_FORMAT = "yyyy-MM-dd'T'00:00:00";
export const SENSORS_FUSION_SELECTION_FORMAT = 'MMM d, yyyy, HH:mm';

export const MAX_POSSIBLE_DATE_ISO = '9999-01-01';
export const MIN_POSSIBLE_DATE_ISO = '-9999-01-01';

export const CAMERA_PLOT_BUFFER_VALUE = 25;

export const OTP_COUNTER_INITIAL_TIME = 600;

export const NOT_MONITORED_DAYS = 7;

export const PASSWORD_PATTERN = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

export enum ASSETS_CATEGORY {
  All = 'all',
  Fixed = 'fixed',
  Mobile = 'mobile',
}

export const VIBRATION_SENSOR_TYPES_KEYWORDS = ['vibration'];
export const TEMPERATURE_SENSOR_TYPES_KEYWORDS = ['temperature', 'temprature', 'pressure'];
export const CONDITION_SENSOR_TYPE_KEYWORDS = ['speed', 'current', 'flow'];
export const OIL_SENSOR_TYPE_KEYWORDS = ['oil-condition', 'oil condition', 'condition', 'quality'];
